"use client";

import { c as _c } from "react-compiler-runtime";
import type { UseQueryOptions } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRetrieveCurrentAssessment } from "@/core/api/orval/ageless-assessments/ageless-assessments";
import { logger } from "@/core/logging";
import type { DisplayAgelessAssessment } from "@orval/model";
import { setMonitoringUserFromAssessment } from "../assessment.monitoring";
import { getScopedCurrentAssessmentQueryKey } from "../currentAssessmentCache";
import { isAuthenticationFailureError, isResettableAssessmentError, isRetryableAssessmentError, shouldSurfaceAssessmentError } from "../assessment-error-policy";
import { getStableImageUrl } from "../utils/imageIdentity";
import { useAssessmentSession } from "./useAssessmentSession";
export type UseCurrentAssessmentOptions = Omit<Partial<UseQueryOptions<DisplayAgelessAssessment, Error>>, "queryKey" | "queryFn">;
type CurrentAssessmentResult = {
  isVerified: boolean;
  isLoadingAssessment: boolean;
  assessment: DisplayAgelessAssessment | undefined;
  error: Error | null;
  shouldResetAssessment: boolean;
  shouldSurfaceAssessmentError: boolean;
  refetch: () => Promise<unknown>;
  isAuthenticated: boolean;
  headers: {
    Authorization: string;
  };
};
export function useCurrentAssessment(options) {
  const $ = _c(49);
  const {
    authorizationHeader,
    error: sessionError,
    reauthenticate,
    status: sessionStatus
  } = useAssessmentSession();
  const isAuthenticated = sessionStatus === "authenticated";
  let t0;
  if ($[0] !== authorizationHeader) {
    t0 = {
      method: "GET",
      headers: {
        Authorization: authorizationHeader
      }
    };
    $[0] = authorizationHeader;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== authorizationHeader) {
    t1 = getScopedCurrentAssessmentQueryKey(authorizationHeader);
    $[2] = authorizationHeader;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isAuthenticated || $[5] !== options || $[6] !== t1) {
    t2 = {
      enabled: isAuthenticated,
      queryKey: t1,
      refetchIntervalInBackground: true,
      retry: _temp,
      ...options
    };
    $[4] = isAuthenticated;
    $[5] = options;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t2) {
    t3 = {
      request: t0,
      query: t2
    };
    $[8] = t0;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data,
    error: assessmentError,
    isLoading,
    refetch
  } = useRetrieveCurrentAssessment(t3);
  let t4;
  bb0: {
    if (!isAuthenticated) {
      t4 = undefined;
      break bb0;
    }
    let t5;
    if ($[11] !== data) {
      t5 = data ? stabilizeAssessmentImages(data) : data;
      $[11] = data;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = t5;
  }
  const assessment = t4;
  const monitoringPersonId = assessment?.personId;
  const monitoringAnonymousId = assessment?.anonymousId;
  const monitoringFirstName = assessment?.firstName;
  let t5;
  let t6;
  if ($[13] !== monitoringAnonymousId || $[14] !== monitoringFirstName || $[15] !== monitoringPersonId) {
    t5 = () => {
      if (!monitoringAnonymousId) {
        return;
      }
      setMonitoringUserFromAssessment({
        personId: monitoringPersonId,
        firstName: monitoringFirstName,
        anonymousId: monitoringAnonymousId
      });
    };
    t6 = [monitoringPersonId, monitoringAnonymousId, monitoringFirstName];
    $[13] = monitoringAnonymousId;
    $[14] = monitoringFirstName;
    $[15] = monitoringPersonId;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[18] !== assessmentError) {
    t7 = () => {
      if (!assessmentError) {
        return;
      }
      logger.error("assessment_retrieval_error", {
        message: assessmentError.message
      });
    };
    t8 = [assessmentError];
    $[18] = assessmentError;
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  const [reauthInFlight, setReauthInFlight] = useState(false);
  const [reauthAttempted, setReauthAttempted] = useState(false);
  const reauthAttemptedRef = useRef(false);
  let t10;
  let t9;
  if ($[21] !== assessmentError || $[22] !== reauthenticate || $[23] !== refetch) {
    t9 = () => {
      if (!assessmentError) {
        if (reauthAttemptedRef.current) {
          reauthAttemptedRef.current = false;
          setReauthAttempted(false);
        }
        return;
      }
      if (!isAuthenticationFailureError(assessmentError)) {
        return;
      }
      if (reauthAttemptedRef.current) {
        return;
      }
      reauthAttemptedRef.current = true;
      setReauthInFlight(true);
      reauthenticate().then(() => refetch()).catch(_temp2).finally(() => {
        setReauthInFlight(false);
        setReauthAttempted(true);
      });
    };
    t10 = [assessmentError, reauthenticate, refetch];
    $[21] = assessmentError;
    $[22] = reauthenticate;
    $[23] = refetch;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  const error_0 = sessionError || assessmentError || null;
  let t11;
  if ($[26] !== assessmentError || $[27] !== reauthAttempted || $[28] !== reauthInFlight) {
    t11 = reauthAttempted && !reauthInFlight && isAuthenticationFailureError(assessmentError);
    $[26] = assessmentError;
    $[27] = reauthAttempted;
    $[28] = reauthInFlight;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const persistentAuthFailure = t11;
  let t12;
  if ($[30] !== error_0 || $[31] !== persistentAuthFailure) {
    t12 = isResettableAssessmentError(error_0) || persistentAuthFailure;
    $[30] = error_0;
    $[31] = persistentAuthFailure;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const shouldResetAssessment = t12;
  let t13;
  if ($[33] !== error_0 || $[34] !== persistentAuthFailure || $[35] !== reauthInFlight) {
    t13 = shouldSurfaceAssessmentError(error_0) && !reauthInFlight && !persistentAuthFailure;
    $[33] = error_0;
    $[34] = persistentAuthFailure;
    $[35] = reauthInFlight;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const surfaceAssessmentError = t13;
  const t14 = Boolean(assessment?.hasVerifiedInfo);
  const t15 = sessionStatus === "resolving" || isLoading || reauthInFlight;
  let t16;
  if ($[37] !== authorizationHeader) {
    t16 = {
      Authorization: authorizationHeader
    };
    $[37] = authorizationHeader;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== assessment || $[40] !== error_0 || $[41] !== isAuthenticated || $[42] !== refetch || $[43] !== shouldResetAssessment || $[44] !== surfaceAssessmentError || $[45] !== t14 || $[46] !== t15 || $[47] !== t16) {
    t17 = {
      isVerified: t14,
      isLoadingAssessment: t15,
      assessment,
      error: error_0,
      shouldResetAssessment,
      shouldSurfaceAssessmentError: surfaceAssessmentError,
      refetch,
      isAuthenticated,
      headers: t16
    };
    $[39] = assessment;
    $[40] = error_0;
    $[41] = isAuthenticated;
    $[42] = refetch;
    $[43] = shouldResetAssessment;
    $[44] = surfaceAssessmentError;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
}
function _temp2(reauthError) {
  logger.error("assessment_reauthenticate_failed", {
    message: reauthError instanceof Error ? reauthError.message : "unknown"
  }, reauthError instanceof Error ? reauthError : new Error(String(reauthError)));
}
function _temp(failureCount, error) {
  return isRetryableAssessmentError(error) && failureCount < 2;
}
function stabilizeAssessmentImages(assessment: DisplayAgelessAssessment): DisplayAgelessAssessment {
  return {
    ...assessment,
    frontFaceImageUrl: getStableImageUrl(assessment.frontFaceImageUrl),
    profileFaceImageUrl: getStableImageUrl(assessment.profileFaceImageUrl),
    futureSelfWithTreatmentImageUrl: getStableImageUrl(assessment.futureSelfWithTreatmentImageUrl),
    futureSelfWithoutTreatmentImageUrl: getStableImageUrl(assessment.futureSelfWithoutTreatmentImageUrl),
    wellnessOriginalImageUrl: getStableImageUrl(assessment.wellnessOriginalImageUrl),
    wellnessWeightLossImageUrl: getStableImageUrl(assessment.wellnessWeightLossImageUrl),
    wellnessMuscleGainImageUrl: getStableImageUrl(assessment.wellnessMuscleGainImageUrl),
    wellnessCombinedImageUrl: getStableImageUrl(assessment.wellnessCombinedImageUrl),
    enhancedLashBrowImageUrl: getStableImageUrl(assessment.enhancedLashBrowImageUrl)
  };
}